// Vendors
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavigateFunction, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Guid } from 'typescript-guid';
import * as amplitude from '@amplitude/analytics-browser';
// Types
import { IUser } from '@appTypes/user';
// Context
import { useToast } from '@context/ToastContext';
import { MessagingState } from '@context/Context';
import { ActionType } from '@context/ActionTypes';
// Services
import DiscussionThreadService from '@services/DiscussionThreadService';
// Components
import ShowInfoModal from './ShowInfoModal';
import MessageModal from '@components/Modals/MessageModal';
import SelectRecipientsModal from '@components/SelectRecipients/SelectRecipientsModal';
import SelectRecipientsWrapper from '@components/SelectRecipients/SelectRecipientsWrapper';
import RecipientSelector from './RecipientSelector';
import TitleAndMessage from './TitleAndMessage';
import ThreadActionButtons from './ThreadActionButtons';
// Hooks
import useModal from '@components/hooks/useModal';
import { useFetchRecipients } from '@root/components/NewDiscussionThread/hooks/useFetchRecipients';
// Payloads
import { IDiscussionThreadCreatePayload, IDiscussionThreadSelectPayload } from '@root/services/payloads/servicePayloads';

export interface NewDiscussionThreadForm {
    recipients: IUser[];
    title: string;
    message: string;
}

const NewDiscussionThread = () => {
    const { t } = useTranslation();
    const {
        state: { message, paginationData },
        dispatch,
    } = MessagingState();
    const { createToast } = useToast();
    const modal = useModal();
    const { folderParam, pageNumber } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const ref = useRef<NavigateFunction>();
    ref.current = navigate;
    const { fetchRecipients } = useFetchRecipients();
    const [modalConfirmed, setModalConfirmed] = useState(false);
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [recipientModalOpen, setRecipientModalOpen] = useState(false);

    // New discussion thread amplitude tracking
    const defaultAmplitudeProps = {
        guid_id: Guid.create().toString(),
    };

    const [amplitudeProps] = useState(defaultAmplitudeProps);

    useEffect(() => {
        amplitude.track('new_message_view_added', amplitudeProps);
    }, [amplitudeProps]);

    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<NewDiscussionThreadForm>({
        defaultValues: {
            recipients: message.recipients,
            title: message.title,
            message: message.message,
        },
    });

    useEffect(() => {
        fetchRecipients(setMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run only once on mount

    const setMessage = useCallback(
        (message: IDiscussionThreadSelectPayload, isInitialLoad?: true) => {
            const searchParams = new URLSearchParams(location.search);
            const showRecipients = searchParams.get('showrecipients') === '1';
            const showReplies = showRecipients ? searchParams.get('collatedreplies') === '1' : false;
            dispatch({
                type: ActionType.SET_MESSAGE,
                payload: {
                    recipients: message.recipients,
                    title: message.title,
                    message: message.message,
                    seeNames: isInitialLoad ? showRecipients : message.seeNames,
                    seeResponses: isInitialLoad ? showReplies : message.seeResponses,
                },
            });
        },
        [dispatch, location.search]
    );

    async function createDiscussionThread() {
        amplitude.track('new_message_form_submitted', amplitudeProps);

        const discussionThread: IDiscussionThreadCreatePayload = {
            message: message.message,
            title: message.title,
            recipientGuids: message.recipients.map(recipient => recipient.roleGuid),
            seeNames: message.seeNames,
            seeResponses: message.seeResponses,
        };

        try {
            const createdThread = await DiscussionThreadService.create(discussionThread);
            setMessage(message);
            if (folderParam === 'sent') {
                const pagedResponsePayload = await DiscussionThreadService.getSent({
                    pageNumber: paginationData.currentPage,
                    pageSize: 10,
                });
                dispatch({
                    type: ActionType.SET_DISCUSSIONTHREADS,
                    payload: pagedResponsePayload.discussionThreads,
                });
                dispatch({
                    type: ActionType.SET_PAGINATION_DATA,
                    payload: {
                        currentPage: pagedResponsePayload.paginationData.currentPage,
                        totalPages: pagedResponsePayload.paginationData.totalPages,
                    },
                });
            }
            dispatch({
                type: ActionType.SET_MESSAGE,
                payload: {
                    title: '',
                    recipients: [],
                    message: '',
                    seeNames: false,
                    seeResponses: false,
                },
            });
            dispatch({
                type: ActionType.SET_SELECTED_RECIPIENTS,
                payload: { options: [] },
            });
            dispatch({
                type: ActionType.SET_LAST_MESSAGE_SENT,
                payload: { threadId: createdThread.data.id, canCancelThread: createdThread.data.canCancelThread },
            });

            // https://github.com/remix-run/react-router/issues/11240
            // Need to use ref, since async call on sent-folder will not cause re-render for navigation
            ref.current && ref.current(`/${folderParam}/page/${pageNumber}/messagesent${location.search}`);
        } catch (error) {
            console.error(error);
            createToast({
                title: t('errors.messageSendingError'),
                toastType: 'danger',
            });
            amplitude.track('error_after_form_submitted', amplitudeProps);
        }
    }

    const onSubmit = () => {
        if (message.title.trim().length === 0) {
            modal.toggle();
        } else {
            createDiscussionThread();
        }
    };

    return (
        <div className="container-fluid h-100">
            <form
                className="d-flex flex-column h-100"
                onSubmit={handleSubmit(onSubmit)}>
                <RecipientSelector
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    setMessage={setMessage}
                    setInfoModalOpen={setInfoModalOpen}
                    amplitudeProps={amplitudeProps}
                    setRecipientModalOpen={setRecipientModalOpen}
                    recipientModalOpen={recipientModalOpen}
                />
                <TitleAndMessage
                    register={register}
                    errors={errors}
                    setMessage={setMessage}
                    amplitudeProps={amplitudeProps}
                />
                <hr />
                <ThreadActionButtons
                    modalConfirmed={modalConfirmed}
                    isSubmitting={isSubmitting}
                    amplitudeProps={amplitudeProps}
                />
            </form>
            <MessageModal
                isShowing={modal.isOpen}
                toggle={modal.toggle}
                title={t('prompts.confirm.noTitleNewMessage')}
                bodyContent={<div />}
                handleConfirm={() => {
                    setModalConfirmed(true);
                    createDiscussionThread();
                }}
            />
            <SelectRecipientsModal
                isShowing={recipientModalOpen}
                toggle={() => setRecipientModalOpen(!recipientModalOpen)}
                bodyContent={<SelectRecipientsWrapper />}
            />
            <ShowInfoModal
                isShowing={infoModalOpen}
                close={() => setInfoModalOpen(false)}
            />
        </div>
    );
};

export default NewDiscussionThread;
